"use client";

import { useEffect, useState } from "react";

// ----------------------------------------------------------------------------
// Fires once the user shows engagement intent OR after a fallback timeout.
//
// Why: Next.js `<Script strategy="lazyOnload">` waits for `window.load`, which
// on a slow mobile connection still happens 3–5s into navigation. After that,
// our third-party trackers (Apollo ~317KB, Clarity, LinkedIn, Metricool) all
// download and execute on the main thread, blocking LCP. Measured Lighthouse
// impact: ~4.7s of "Script Evaluation" before the hero can paint.
//
// Strategy: wait for the first scroll, touch, keydown, or pointerdown. If the
// user is still on the page after `fallbackMs` (default 5s) without any of
// those, fire anyway so we don't lose bounce-tracking entirely.
//
// Returns `true` once the gate has fired; trackers conditionally render their
// `<Script>` on that signal. SSR returns `false` so the script tags never make
// it into the initial HTML.
// ----------------------------------------------------------------------------

const INTERACTION_EVENTS = [
  "scroll",
  "touchstart",
  "pointerdown",
  "keydown",
] as const;

export default function useInteractionDeferred(fallbackMs = 5000): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;

    let cancelled = false;
    const fire = () => {
      if (cancelled) return;
      cancelled = true;
      setReady(true);
    };

    const opts: AddEventListenerOptions = { once: true, passive: true };
    INTERACTION_EVENTS.forEach((ev) =>
      window.addEventListener(ev, fire, opts),
    );

    const timeoutId = window.setTimeout(fire, fallbackMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      INTERACTION_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, fire),
      );
    };
  }, [ready, fallbackMs]);

  return ready;
}
